import TableButton from 'components/Buttons/TableButtons/TableButton'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { FilterCriteriaSelector } from 'components/Filters/components/FilterCriteriaSelector'
import { FilterStore } from 'components/Filters/FilterStore'
import { countActiveFilters } from 'components/Filters/filterCount'
import { cn } from 'components/UI/cn'
import { Dialog, DialogContent, DialogClose } from 'components/UI/Modal'
import FbButton from 'components/FbUI/FbButton'
import { useMediaQuery } from 'hooks/useMediaQuery'
import { useMountEffect } from 'hooks/useMountEffect'
import { FilterIdentifier } from 'models/saved_view'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { PiFloppyDisk, PiFunnel, PiXBold } from 'react-icons/pi'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components/macro'
import { CreateSavedFilterModal } from '../saved-filters/CreateSavedFilterModal'
import SelectSavedFilter from '../saved-filters/SelectSavedFilter'
import { PiCaretDown, PiCaretUp } from 'react-icons/pi'
import { blurActiveElement } from 'utils/focus'
import { FilterSortDropdown, FilterSortMode } from './FilterSortDropdown'

/* -----------------------------------------------------------------------
 *  Context plumbing for auto-counted FilterGroup bubbles
 * ----------------------------------------------------------------------- */

// FilterModal provides its `store` here so descendant FilterGroups can read
// pending filter state without each filterset wiring it through manually.
const FilterStoreContext = createContext<FilterStore | null>(null)

// Each FilterGroup provides a `registerKey` function. Child filter
// components (SelectFilter, RangeFilter, …) call `useRegisterFilterKey`
// on mount to add their filterKey to the nearest group's tally, so the
// collapsed-state count bubble shows up everywhere without per-filterset
// wiring.
const FilterGroupRegistrationContext = createContext<{
  registerKey: (key: string) => () => void
} | null>(null)

/**
 * Filter components call this with their filterKey. It registers the key
 * with the nearest enclosing FilterGroup (if any) and unregisters on
 * unmount, so the group can compute its active-filter count automatically.
 *
 * Uses `useMountEffect` rather than a deps-driven `useEffect`: `ctx` and
 * `key` are stable for the lifetime of a filter component (the context
 * provider's value is memoized in FilterGroup, the key is a constant
 * prop), so a once-on-mount registration + cleanup is the right shape
 * and keeps this within the project's "no raw useEffect" rule.
 */
export function useRegisterFilterKey(key: string | undefined) {
  const ctx = useContext(FilterGroupRegistrationContext)
  useMountEffect(() => {
    if (!ctx || !key) return
    return ctx.registerKey(key)
  })
}

export default function FilterModal({
  children,
  store,
  identifier,
  disableSavedFilters,
  buttonText = 'Filters',
  sortable = false,
}: {
  store: FilterStore
  identifier: FilterIdentifier
  children?: React.ReactNode
  disableSavedFilters?: boolean
  buttonText?: string
  /** Opt-in to the Category / A–Z / Z–A sort dropdown. Use for long
   *  filtersets (Doors, Chains, Campaign Targets) where finding a specific
   *  filter alphabetically is faster than scanning categories. */
  sortable?: boolean
}) {
  const [openFilterSaveModal, setOpenFilterSaveModal] = React.useState(false)
  const [openFilterModal, setOpenFilterModal] = React.useState(false)
  const [sortMode, setSortMode] = useState<FilterSortMode>('category')
  // Lifted here (instead of living inside SelectSavedFilter) so the modal's
  // "Clear all" can wipe the pill in lockstep with the filter state.
  const [savedFilterValue, setSavedFilterValue] = useState<
    string | number | null
  >(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const linkVariant = useTableLinkVariant()
  // Below sm (640px) the header can't fit the labeled saved-filters pill next
  // to the title + close, so we move it to its own full-width row at the top
  // of the modal body (FIR-7788). Matches SelectSavedFilter's own `sm` collapse
  // point so the pill is either in the header (desktop) or the body (phone),
  // never both.
  const isPhone = useMediaQuery('(max-width: 639px)')

  const {
    applyFilters,
    setPendingCriteria,
    pendingCriteria,
    valueFilters,
    rangeFilters,
    zipCodeFilter,
    search,
    pendingValueFilters,
    pendingRangeFilters,
    pendingZipCodeFilter,
    pendingSearch,
    syncPendingFilters,
    clearAllPendingFilters,
  } = store()

  const filterCount = useMemo(
    () =>
      countActiveFilters({ valueFilters, rangeFilters, search, zipCodeFilter }),
    [valueFilters, rangeFilters, search, zipCodeFilter]
  )

  // Pending-side counter: drives whether the "Show results matching X
  // of the following criteria:" footer renders at all. Hidden when no
  // filters are staged so users aren't shown a criteria line with
  // nothing beneath it.
  const pendingFilterCount = useMemo(
    () =>
      countActiveFilters({
        valueFilters: pendingValueFilters,
        rangeFilters: pendingRangeFilters,
        search: pendingSearch,
        zipCodeFilter: pendingZipCodeFilter,
      }),
    [
      pendingValueFilters,
      pendingRangeFilters,
      pendingSearch,
      pendingZipCodeFilter,
    ]
  )

  useEffect(() => syncPendingFilters(), [openFilterModal])

  const removePageFromUrlSearchParams = () => {
    const searchParamsCopy = new URLSearchParams(searchParams)
    searchParamsCopy.delete('page')
    setSearchParams(searchParamsCopy)
  }

  return (
    <>
      <TableButton
        text={buttonText}
        icon={PiFunnel}
        activeValue={filterCount > 0 ? filterCount.toString() : null}
        postpendIcon={
          filterCount > 0 && !disableSavedFilters ? PiFloppyDisk : undefined
        }
        onPostpendIconClick={() => {
          blurActiveElement()
          setOpenFilterSaveModal(true)
        }}
        onClick={() => {
          blurActiveElement()
          setOpenFilterModal(true)
        }}
        type="button"
        hugContent={linkVariant === 'plain'}
      />
      <Dialog
        open={openFilterModal}
        onOpenChange={(open) => setOpenFilterModal(open)}
      >
        <DialogContent
          size="lg"
          noPadding
          disableCloseIcon
          className="!gap-0 !rounded-xl !py-0"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 px-6 pb-3 pt-6">
            <h2 className="m-0 text-2xl font-semibold leading-8 tracking-[-0.2px] text-neutral-800">
              All filters
            </h2>
            <div className="flex flex-shrink-0 items-center gap-2">
              {!disableSavedFilters && !isPhone && (
                <SelectSavedFilter
                  filterStore={store}
                  identifier={identifier}
                  value={savedFilterValue}
                  onChange={setSavedFilterValue}
                />
              )}
              {sortable && (
                <FilterSortDropdown value={sortMode} onChange={setSortMode} />
              )}
              <DialogClose asChild>
                <button
                  type="button"
                  aria-label="Close"
                  className="flex items-center justify-center rounded-lg p-2.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-500"
                >
                  <PiXBold size={20} />
                </button>
              </DialogClose>
            </div>
          </div>

          {/* Body. `overflow-x-visible` + an inner px-1 buffer so the
              brand-active 4px focus ring on the right-most pill in each row
              isn't clipped against the scroll container edge. */}
          <div className="ds-modal-scroll min-h-0 flex-1 overflow-y-auto px-6 pb-4">
            <FilterStoreContext.Provider value={store}>
              <div className="px-1">
                {/* On phones the labeled saved-filters pill lives here, as its
                    own full-width row, instead of the header (FIR-7788). */}
                {!disableSavedFilters && isPhone && (
                  <div className="mb-4">
                    <SelectSavedFilter
                      fullWidth
                      filterStore={store}
                      identifier={identifier}
                      value={savedFilterValue}
                      onChange={setSavedFilterValue}
                    />
                  </div>
                )}
                {sortable && sortMode !== 'category' ? (
                  <AlphabeticalFilterList sortMode={sortMode}>
                    {children}
                  </AlphabeticalFilterList>
                ) : (
                  children
                )}
              </div>
            </FilterStoreContext.Provider>
          </div>

          {/* Footer */}
          <div className="flex flex-col gap-3 border-t border-line px-6 py-4">
            {pendingFilterCount > 0 && (
              <>
                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-neutral-700">
                  Show results matching
                  <FilterCriteriaSelector
                    value={pendingCriteria}
                    onChange={setPendingCriteria}
                  />
                  of the following:
                </div>
                <FilterChips
                  identifier={identifier}
                  store={store}
                  className="!ml-0 !mt-0"
                />
              </>
            )}
            <div className="flex items-center justify-between">
              {pendingFilterCount > 0 ? (
                <button
                  type="button"
                  onClick={() => {
                    clearAllPendingFilters()
                    // Reset the saved-filter pill in lockstep — otherwise it
                    // keeps reading "Q1 Prospects" after the user clears.
                    setSavedFilterValue(null)
                  }}
                  className="py-2.5 text-sm font-medium text-brand-active transition-colors hover:text-brand-bright"
                >
                  Clear all
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <DialogClose asChild>
                  <FbButton variant="secondary" type="button">
                    Cancel
                  </FbButton>
                </DialogClose>
                <FbButton
                  type="button"
                  className="!bg-brand-bright !text-white hover:!bg-brand-active active:!bg-brand-active disabled:!bg-neutral-100 disabled:!text-neutral-400 disabled:!border-neutral-100 disabled:hover:!bg-neutral-100"
                  // Disabled when there's nothing to apply and nothing
                  // currently applied — clicking would be a true no-op.
                  // Stays enabled when filters ARE applied even if pending
                  // is empty, so the user can intentionally commit the
                  // cleared state.
                  disabled={pendingFilterCount === 0 && filterCount === 0}
                  onClick={() => {
                    applyFilters()
                    removePageFromUrlSearchParams()
                    setOpenFilterModal(false)
                  }}
                >
                  Apply
                </FbButton>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {!disableSavedFilters && (
        <CreateSavedFilterModal
          show={openFilterSaveModal}
          handleClose={() => setOpenFilterSaveModal(false)}
          identifier={identifier}
          filterStore={store}
        />
      )}
    </>
  )
}

/**
 * Returns the number of currently-pending filters in `filterKeys` so a parent
 * filterset can pass `activeCount` to a collapsible <FilterGroup> and surface
 * the DS count bubble next to the section title.
 *
 * Counts value-filters (multi-select), range filters with either min or max
 * set, search filters with a value, and the zipcode tuple (when the key
 * literal "zipcode" appears in the list).
 */
export function useFilterGroupActiveCount(
  filterStore: FilterStore,
  filterKeys: readonly string[]
): number {
  const {
    pendingValueFilters,
    pendingRangeFilters,
    pendingSearch,
    pendingZipCodeFilter,
  } = filterStore()

  let count = 0
  for (const key of filterKeys) {
    if (pendingValueFilters[key]?.length) count++
    const r = pendingRangeFilters[key]
    if (r && (r.min != null || r.max != null)) count++
    if (pendingSearch[key]?.value) count++
    if (key === 'zipcode' && pendingZipCodeFilter[0].length > 0) count++
  }
  return count
}

export function QuickFilterGroup({ children }: { children: React.ReactNode }) {
  // FilterGroup already paints a `border-t` separator at the top of each
  // subsequent group, so no extra Divider here — that's what produced the
  // duplicate rule under Quick Filters.
  return <FilterGroup title="Quick Filters">{children}</FilterGroup>
}

export function MobileFilterGroup({
  children,
  fontStyle,
  title,
  className,
}: {
  children: React.ReactNode
  fontStyle?: string
  title: string
  className?: string
}) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className={'mt-3 mb-2'}>
      <div
        className={'text-md font-medium mb-2 flex justify-between'}
        style={{ fontStyle }}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span>{title}</span>
        {isOpen ? (
          <PiCaretUp size={18} className="text-neutral-500" />
        ) : (
          <PiCaretDown size={18} className="text-neutral-500" />
        )}
      </div>
      {isOpen && (
        <div className={cn('flex flex-col gap-2', className)}>{children}</div>
      )}
    </div>
  )
}

export function FilterGroup({
  children,
  fontStyle,
  title,
  className,
  defaultCollapsed,
  disableCollapse = false,
  activeCount,
}: {
  children: React.ReactNode
  fontStyle?: string
  title: string
  className?: string
  defaultCollapsed?: boolean
  /** Hide the chevron and lock the group open. Use when a modal only
   *  has a single section — there's nothing meaningful to collapse to. */
  disableCollapse?: boolean
  /** Optional manual override for the collapsed-state count bubble. If
   *  omitted, the group auto-counts by tracking which filter components
   *  register through the FilterGroupRegistrationContext — so every
   *  filterset using <FilterGroup> gets the bubble for free. */
  activeCount?: number
}) {
  // Default: collapsed on mobile (< 1024px), expanded on desktop
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const [isCollapsed, setIsCollapsed] = useState(
    disableCollapse ? false : (defaultCollapsed ?? false)
  )

  // Sync with mobile state on mount/resize
  useEffect(() => {
    if (disableCollapse) return
    if (defaultCollapsed === undefined) {
      setIsCollapsed(isMobile)
    }
  }, [isMobile, defaultCollapsed, disableCollapse])

  // Auto-count tracking: child filter components call useRegisterFilterKey
  // with their filterKey; we keep a set here and read the count from a
  // child subscriber so the active-count hook only runs when there's an
  // ambient FilterStoreContext (e.g. inside FilterModal). Components using
  // FilterGroup outside of FilterModal — like the legacy EventHistory modal
  // that uses its own store — render fine with autoCount = 0.
  const [registeredKeys, setRegisteredKeys] = useState<readonly string[]>([])
  const registerKey = useCallback((key: string) => {
    setRegisteredKeys((prev) => (prev.includes(key) ? prev : [...prev, key]))
    return () => {
      setRegisteredKeys((prev) => prev.filter((k) => k !== key))
    }
  }, [])
  const registrationValue = useMemo(() => ({ registerKey }), [registerKey])

  const filterStore = useContext(FilterStoreContext)
  const [autoCount, setAutoCount] = useState(0)
  const effectiveCount = activeCount ?? autoCount

  return (
    <FilterGroupRegistrationContext.Provider value={registrationValue}>
      {filterStore && (
        <FilterGroupAutoCountSubscriber
          filterStore={filterStore}
          filterKeys={registeredKeys}
          onChange={setAutoCount}
        />
      )}
      <div className="py-[16px] border-t border-line first:border-t-0 first:pt-0">
        <div
          className={cn(
            'flex items-center justify-between pr-3',
            !disableCollapse && 'cursor-pointer'
          )}
          onClick={
            disableCollapse ? undefined : () => setIsCollapsed(!isCollapsed)
          }
        >
          <span className="inline-flex items-center gap-2">
            <span
              className="text-base font-medium tracking-[-0.2px] text-neutral-500"
              style={{ fontStyle }}
            >
              {title}
            </span>
            {isCollapsed && effectiveCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-basil-50 text-brand-active text-xs font-semibold tabular-nums">
                {effectiveCount}
              </span>
            )}
          </span>
          {!disableCollapse &&
            (isCollapsed ? (
              <PiCaretDown size={18} className="text-neutral-500" />
            ) : (
              <PiCaretUp size={18} className="text-neutral-500" />
            ))}
        </div>
        <div
          className={cn(
            'grid transition-all duration-200 ease-in-out overflow-hidden',
            isCollapsed ? 'grid-rows-[0fr] mt-0' : 'grid-rows-[1fr] mt-2'
          )}
        >
          <div className="min-h-0">
            <div
              className={cn(
                'grid grid-cols-2 lg:grid-cols-3 gap-2 p-1',
                className
              )}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </FilterGroupRegistrationContext.Provider>
  )
}

/**
 * Renders nothing — it just calls `useFilterGroupActiveCount` (a Zustand
 * subscription) and propagates the count up. Conditionally mounted by
 * FilterGroup only when a FilterStore is provided via context, so we never
 * try to subscribe to a null store (rules-of-hooks safe).
 *
 * Uses a raw `useEffect` rather than `useMountEffect` because `count`
 * changes reactively as filters mutate — that's the whole point of this
 * subscriber. This is a deliberate exception to the "no raw useEffect"
 * rule: we're bridging a Zustand-derived value out of a hook scope into
 * a parent that can't subscribe directly (the parent's enclosing
 * FilterStoreContext may be null, so it can't call the hook itself).
 * Don't copy this pattern unless you have the same constraint.
 */
function FilterGroupAutoCountSubscriber({
  filterStore,
  filterKeys,
  onChange,
}: {
  filterStore: FilterStore
  filterKeys: readonly string[]
  onChange: (count: number) => void
}) {
  const count = useFilterGroupActiveCount(filterStore, filterKeys)

  useEffect(() => {
    onChange(count)
  }, [count, onChange])
  return null
}

export const Divider = styled.hr`
  border: 1px solid #e5e5e5;
  margin: 16px 0;
`

/* -----------------------------------------------------------------------
 *  Alphabetical sort mode
 * -----------------------------------------------------------------------
 *
 *  In Category mode, FilterModal renders the filterset's children as-is —
 *  authored FilterGroups with hand-picked sections. In A–Z / Z–A mode, we
 *  flatten the tree into a single list bucketed by the first letter of each
 *  filter's title.
 *
 *  How we identify a "filter" in the tree:
 *    1. Element with a `title` string prop (direct usage of RangeFilter,
 *       SelectFilter, etc. via `helper.retrieveProps(...)`).
 *    2. Composite components that hardcode their title internally
 *       (AvgRatingRangeFilter, ZipcodeFilter, …) declare a static
 *       `sortMeta = { title }` on the function component so we can read it
 *       without rendering.
 *
 *  Filtersets opting into sortable layout must ensure every filter is
 *  reachable via React.Children — composites that hide many filters behind
 *  a single function component (e.g. Common*AccountGroup) expose a
 *  `useXFilters()` hook that returns the inner JSX as a Fragment, and the
 *  filterset renders that Fragment directly inside its own <FilterGroup>.
 *
 *  QuickFilterGroup contents are skipped in alphabetical mode — quick
 *  filters share their title with the underlying filter elsewhere in the
 *  modal, so including both produces confusing duplicates.
 */

type FilterSortItem = {
  title: string
  element: React.ReactElement
  /** Stable key per item — survives re-bucketing. */
  key: string
}

type SortMeta = { title?: string }

function readSortMeta(type: unknown): SortMeta | undefined {
  if (typeof type !== 'function' && typeof type !== 'object') return undefined
  return (type as { sortMeta?: SortMeta }).sortMeta
}

function collectFilterItems(
  node: React.ReactNode,
  acc: { items: FilterSortItem[] }
) {
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) return
    const props = child.props as { title?: unknown; children?: React.ReactNode }

    // QuickFilterGroup: skip entirely. Its filters share titles with the
    // underlying filter elsewhere in the modal — including both would
    // produce duplicate letter-bucket entries.
    if (child.type === QuickFilterGroup) return

    // FilterGroup / MobileFilterGroup: descend into children, do not emit.
    if (child.type === FilterGroup || child.type === MobileFilterGroup) {
      collectFilterItems(props.children, acc)
      return
    }

    // Fragments: descend.
    if (child.type === React.Fragment) {
      collectFilterItems(props.children, acc)
      return
    }

    // Direct filter element with a `title` prop. Fallback key uses the
    // accumulator's running length — globally unique even when two
    // filters share a title across different FilterGroups (per-group
    // React.Children indices would have collided).
    if (typeof props.title === 'string' && props.title.length > 0) {
      const filterKey = (props as { filterKey?: unknown }).filterKey
      acc.items.push({
        title: props.title,
        element: child,
        key:
          typeof filterKey === 'string'
            ? filterKey
            : `__pos${acc.items.length}__${props.title}`,
      })
      return
    }

    // Composite with static metadata (e.g. ZipcodeFilter, CityAndStateFilter).
    const meta = readSortMeta(child.type)
    if (meta?.title) {
      acc.items.push({
        title: meta.title,
        element: child,
        key: `__pos${acc.items.length}__meta-${meta.title}`,
      })
      return
    }
    // Unknown / opaque element: drop quietly. Filtersets opting into
    // sortable layout should ensure every filter is either title-bearing
    // or annotated with sortMeta.
  })
}

function AlphabeticalFilterList({
  children,
  sortMode,
}: {
  children?: React.ReactNode
  sortMode: 'asc' | 'desc'
}) {
  // Walk the children tree once per render. We don't memoize on `children`
  // because the prop is a fresh React node each parent render — reference
  // equality never holds, so a useMemo here would always re-run anyway and
  // just add noise. The walk is O(n) on ~60 filters, well under a frame.
  const acc = { items: [] as FilterSortItem[] }
  collectFilterItems(children, acc)
  const { items } = acc

  const sorted = (() => {
    const cmp = (a: FilterSortItem, b: FilterSortItem) =>
      a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
    const arr = [...items].sort(cmp)
    if (sortMode === 'desc') arr.reverse()
    return arr
  })()

  const letterBuckets: Array<{ letter: string; items: FilterSortItem[] }> = []
  for (const item of sorted) {
    const letter = (item.title[0] ?? '#').toUpperCase()
    const last = letterBuckets[letterBuckets.length - 1]
    if (last && last.letter === letter) last.items.push(item)
    else letterBuckets.push({ letter, items: [item] })
  }

  return (
    <div>
      {letterBuckets.map(({ letter, items }) => (
        <div
          key={letter}
          className="py-[16px] border-t border-line first:border-t-0 first:pt-0"
        >
          <div className="flex items-center pr-3">
            <span className="text-base font-medium tracking-[-0.2px] text-neutral-500">
              {letter}
            </span>
          </div>
          <div className="mt-2 grid grid-cols-2 lg:grid-cols-3 gap-2 p-1">
            {items.map((item) => (
              <React.Fragment key={item.key}>{item.element}</React.Fragment>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
